import { EhrConnector, MeProfile } from "@elation/api-client/src/stitch-web";

import { toErrorMessage } from "@src/shared/utils/errors";

import { events, type LoginMethod } from "./events";
import * as identify from "./identify";

export type { LoginMethod };

/**
 * Builds the iframe-flavored `LoginMethod` for a given EHR connector
 * (e.g. `iframe_elation`). Stays in sync with the `EhrConnector` enum.
 */
export function iframeLoginMethod(ehr: EhrConnector): LoginMethod {
  return `iframe_${ehr}` as LoginMethod;
}

/**
 * Records a post-auth session-initialization failure (auth succeeded but
 * `/me` failed). Reported as `app error` with `surface: "session_init"` so
 * the `login failed` metric stays clean for actual credential failures.
 */
export function trackSessionInitFailure(
  method: LoginMethod,
  err: unknown,
): void {
  events.appError.track({
    surface: "session_init",
    error: toErrorMessage(err, "Session initialization failed"),
    error_code: "me_fetch_failed",
    error_source: method,
    fatal: false,
  });
}

/**
 * Identifies the authenticated user on the active Datadog RUM session and
 * emits a `user logged in` and `start session` event. Called from each login
 * completion path.
 *
 * Safe to call before RUM initializes (tests, MSW mocking, no Datadog token):
 * `datadogRum` no-ops in that case, so callers don't need to guard themselves.
 */
export function trackUserLoggedIn(
  loginResponse: MeProfile,
  method: LoginMethod,
): void {
  identify.setUser(loginResponse);
  events.userLoggedIn.track({ method });
  events.startSession.track();
}
